import { render } from "lit-html";
import { Component, prop } from "@kluntje/core";
import { I18nService } from "Services/I18nService";
import { HIDDEN, LOADING } from "Helper/cssClasses";
import { addClass, removeClass, waitForTransitionEnd } from "@kluntje/js-utils/lib/dom-helpers";
import { fetchJSON } from "Helper/apiHelper";
import { autobind } from "Helper/functionHelper/decorators";
import FRABreadcrumb from "Components/fra-m-breadcrumb/fra-m-breadcrumb";

import { parkBookingAddressTemplate, parkBookingDataTemplate, parkBookingTemplate } from "./fra-m-park-booking__list.template"; // eslint-disable-line
import type {
  ActionResponse,
  Booking,
  BookingJsonResponse,
  BookingsJsonResponse,
  DetailedBooking,
} from "./fra-m-park-bookings-interfaces";

const SLIDE_OUT = "fra-m-park-bookings__details-wrapper--slide-out";

const BOOKING_HASH_PREFIX = "#booking-";
export function createHashFromBookingCode(bookingCode: string) {
  return BOOKING_HASH_PREFIX + bookingCode;
}

export function getBookingCodeFromHash(hash: string) {
  return hash.replace(BOOKING_HASH_PREFIX, "");
}

/**
 * Custom Element to fetch park bookings and park booking details
 */
class FRAParkBookings extends Component {
  // AJAX endpoint for all bookings for current user
  @prop({ required: true })
  bookingsUrl = "";

  // AJAX endpoint for details of an individual booking
  @prop({ required: true })
  bookingDataUrl = "";

  // (in seconds) interval duration where the data are re-fetched from the server to check e.g. if bookings were canceled in the meantime
  @prop({ type: "number", defaultValue: 60 })
  polingInterval = 60;

  // booking number for the park booking which the ser is currently seeing its details
  private _selectedBookingsCode = "";

  // user has selected a parking booking and the detail view was opened on this user interaction
  private _detailViewOpened = false;

  private _bookingsData: Booking[] = [];

  constructor() {
    super({
      ui: {
        bookingList: ".fra-js-park-booking__list:-one",
        bookingData: ".fra-js-park-booking__data:-one",
        bookingAddress: ".fra-m-park-booking__address:-one",
        bookingCancellationRequest: ".fra-m-park-booking__data___cancellation",
        bookingCancellationRefund: ".fra-m-park-booking__data___refund:-one",
        bookingBackButton: ".fra-js-park-booking__back:-one",
        bookingOverviewWrapper: ".fra-m-park-bookings__overview-wrapper:-one",
        bookingDetailsWrapper: ".fra-m-park-bookings__details-wrapper:-one",
        customerEmail: "span[data-customer-email]",
        input: ".fra-m-park-bookings__input :-one",
        invoiceActionButton: ".fra-js-park-booking__invoice:-one",
        parkcardActionButton: ".fra-js-park-booking__parkcard:-one",
        cancellationActionButton: ".fra-js-park-booking__cancellation:-one",
        refundActionButton: ".fra-js-park-booking__refund:-one",
      },

      events: [
        {
          event: "click",
          target: "bookingBackButton",
          handler: "handleBackButton",
        },
        {
          event: "click",
          target: "invoiceActionButton",
          handler: "handleBookingAction",
        },
        {
          event: "click",
          target: "parkcardActionButton",
          handler: "handleBookingAction",
        },
        {
          event: "click",
          target: "cancellationActionButton",
          handler: "handleBookingAction",
        },
        {
          event: "click",
          target: "refundActionButton",
          handler: "handleBookingAction",
        },
        {
          event: "hashchange",
          target: "window",
          handler: "onHashChange",
        },
        {
          event: "popstate",
          target: "window",
          handler: "closeDetailView",
        },
      ],
    });
  }

  /**
   * Lifecycle-Hook
   * @override
   */
  async afterComponentRender(): Promise<void> {
    await I18nService.getInstance().waitForJson();

    // is a deeplink to detailed view of an parking booking
    if (location.hash.startsWith(BOOKING_HASH_PREFIX)) {
      this.showBookingData(getBookingCodeFromHash(location.hash));
    }

    this.fetchAndRenderBookingsOverviewList();
  }

  async fetchAndRenderBookingsOverviewList() {
    let response: BookingsJsonResponse | null | undefined;

    try {
      response = await this.getData<BookingsJsonResponse>(`${this.bookingsUrl}?time=${Date.now()}`);
    } catch {}

    if (response?.bookings?.length) {
      this._bookingsData = response.bookings;
      this.renderBookingsOverviewList(response.bookings);
    } else {
      this.ui.bookingList.innerHTML = `<h3 class="fra-h3 fra-m-park-bookings__message">${I18nService.getInstance().getKey(
        response?.bookings?.length === 0
          ? "fraport.travel.components.parkBooking.no.data.label"
          : "fraport.travel.components.parkBooking.error.data.label",
      )}</h3>`;
    }

    // user can (some times has to) cancel the parking on the https://parken.frankfurt-airport.com/ page. poling the status in case the parking was canceled in the meanwhil
    setTimeout(() => {
      this.fetchAndRenderBookingsOverviewList();
    }, this.polingInterval * 1000);
  }

  onHashChange(_e: HashChangeEvent) {
    // e.g. on page forward
    if (location.hash.startsWith(BOOKING_HASH_PREFIX)) {
      const bookingCode = getBookingCodeFromHash(location.hash);
      this.showBookingData(bookingCode);
    }
  }

  handleBookingAction(e: Event): void {
    const ctaBtn = e.currentTarget as HTMLElement;
    const bookingCode = this._selectedBookingsCode;
    const actionUrl = `${ctaBtn.dataset.actionUrl}/${bookingCode}/action.json?time=${Date.now()}`;

    this.getData<ActionResponse>(actionUrl)
      .then(response => {
        this.showResponseStatus(ctaBtn, !response || response.error);
      })
      .catch(_error => {
        this.showResponseStatus(ctaBtn, true);
      });
  }

  showResponseStatus(ctaBtn: any, error: boolean): void {
    const actionContainer = ctaBtn.closest(".fra-m-park-booking__action");
    const successMsg = actionContainer.querySelector(".fra-m-park-booking__success");
    const failMsg = actionContainer.querySelector(".fra-m-park-booking__fail");
    addClass(ctaBtn, HIDDEN);
    if (!error) {
      removeClass(successMsg, HIDDEN);
      addClass(failMsg, HIDDEN);
    } else {
      removeClass(failMsg, HIDDEN);
      addClass(successMsg, HIDDEN);
    }
  }

  handleBackButton(_e: UIEvent | PopStateEvent): void {
    // i.e. not started with detail view opened on page load via a deeplink
    if (this._detailViewOpened) {
      history.back();
    } else {
      history.replaceState(null, "", location.href.replace(location.hash, ""));
      this.closeDetailView();
    }
  }

  async closeDetailView() {
    document.querySelector<FRABreadcrumb>("fra-m-breadcrumb")?.removeLastBreadcrumb();
    addClass(this.ui.bookingDetailsWrapper, SLIDE_OUT);
    await waitForTransitionEnd(this.ui.bookingDetailsWrapper);
    removeClass(this.ui.bookingOverviewWrapper, HIDDEN);
    removeClass(this.ui.bookingOverviewWrapper, SLIDE_OUT);
    addClass(this.ui.bookingDetailsWrapper, HIDDEN);
  }

  resetActionContainers(): void {
    this.resetActionContainer(this.ui.invoiceActionButton);
    this.resetActionContainer(this.ui.cancellationActionButton);
    this.resetActionContainer(this.ui.parkcardActionButton);
    this.resetActionContainer(this.ui.refundActionButton);
  }

  resetActionContainer(element: any): void {
    if (element !== null && element !== undefined) {
      removeClass(element, HIDDEN);
      const actionContainer = element.closest(".fra-m-park-booking__action");
      addClass(actionContainer.querySelector(".fra-m-park-booking__success"), HIDDEN);
      addClass(actionContainer.querySelector(".fra-m-park-booking__fail"), HIDDEN);
    }
  }

  @autobind
  showBookingData(bookingCode: string, pushState = false): void {
    this._selectedBookingsCode = bookingCode;
    if (pushState) {
      this._detailViewOpened = true;
      history.pushState(null, "", createHashFromBookingCode(bookingCode));
      // scroll to page top according to the user story,
        window.scrollTo({
          top: 0,
        behavior: "auto",
        });
    }
    document.querySelector<FRABreadcrumb>("fra-m-breadcrumb")?.addBreadcrumb(`#${bookingCode}`);
    this.resetActionContainers();
    this.switchToBookingData();
    // incase the bookings were fetched earlier (i.e. not a page load for a deep link to a particular booking),
    // render the basic information, before the details are fetched and shown
    const bookingsBasicData = this._bookingsData.find(booking => booking.bookingCode === bookingCode);
    render(parkBookingDataTemplate((bookingsBasicData || { bookingCode }) as DetailedBooking), this.ui.bookingData);

    this.classList.add(LOADING);

    this.fetchAndRenderBookingsDetailView();
  }

  async fetchAndRenderBookingsDetailView() {
    if (!this._selectedBookingsCode) return;

    const bookingDataUrl = `${this.bookingDataUrl}/${this._selectedBookingsCode}/booking.json?time=${Date.now()}`;

    let response: BookingJsonResponse | null | undefined;

    try {
      response = await this.getData<BookingJsonResponse>(bookingDataUrl);
    } catch {}

    this.classList.remove(LOADING);

    if (response?.booking) {
      this.renderBookingDetailView(response.booking);

      if (response.booking.canceled) {
        removeClass(this.ui.bookingCancellationRefund, HIDDEN);
        addClass(this.ui.bookingCancellationRequest, HIDDEN);
        this.ui.parkcardActionButton.disabled = true;
        this.ui.invoiceActionButton.disabled = true;
      } else {
        addClass(this.ui.bookingCancellationRefund, HIDDEN);
        removeClass(this.ui.bookingCancellationRequest, HIDDEN);
        this.ui.parkcardActionButton.disabled = false;
        this.ui.invoiceActionButton.disabled = false;
      }
    } else {
      this.ui.bookingData.innerHTML = `<h3 class="fra-h3 fra-m-park-bookings__message">${I18nService.getInstance().getKey(
        "fraport.travel.components.parkBooking.error.data.label",
      )}</h3>`;
    }

    // user can (some times has to) cancel the parking on the https://parken.frankfurt-airport.com/ page. poling the status in case the parking was canceled in the meanwhile
    setTimeout(() => {
      this.fetchAndRenderBookingsDetailView();
    }, this.polingInterval * 1000);
  }

  switchToBookingData(): void {
    addClass(this.ui.bookingOverviewWrapper, HIDDEN);
    addClass(this.ui.bookingDetailsWrapper, SLIDE_OUT);
    removeClass(this.ui.bookingDetailsWrapper, HIDDEN);
    // double raf to change after the current styling is applied
    requestAnimationFrame(() => requestAnimationFrame(() => removeClass(this.ui.bookingDetailsWrapper, SLIDE_OUT)));
  }

  /**
   * Rendering booking items
   *
   * @param {Booking[] | null | undefined} bookings - booking response object
   */
  async renderBookingsOverviewList(bookings: Booking[]): Promise<void> {
    await I18nService.getInstance().waitForJson();

    const input = parkBookingTemplate(bookings, this.showBookingData);
    render(input, this.ui.bookingList);
  }

  /**
   * Rendering booking items
   *
   * @param {DetailedBooking} booking - booking response object
   */
  async renderBookingDetailView(booking: DetailedBooking): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const details = parkBookingDataTemplate(booking);
    const address = parkBookingAddressTemplate(booking.customerAddress);
    render(details, this.ui.bookingData);
    render(address, this.ui.bookingAddress);
    if (this.ui.customerEmail && booking && booking.customerAddress && booking.customerAddress.email) {
      this.ui.customerEmail.forEach((element: any) => {
        element.innerHTML = booking.customerAddress.email;
      });
    }
  }

  async getData<T>(endpointUrl: string) {
    try {
      const response = await fetchJSON<T>(endpointUrl);
      return response;
    } catch (error) {
      console.error("ParkBookingsService: ", error);
      throw error;
    }
  }
}

customElements.define("fra-m-park-bookings", FRAParkBookings);

export default FRAParkBookings;
